/**
 * @file LifeTaskRow.tsx
 * @description One task row for the life-layer pages (Today / Tasks): done
 * checkbox, priority dot (HIGH red / MEDIUM amber / LOW gray), title, due-date
 * label ("dziś HH:MM" / "jutro" / red "zaległe"), Top-3 star toggle and the
 * domain badge (color dot + name). Mutations are delegated to the parent via
 * callbacks; the row only renders state.
 */

import { useTranslation } from "react-i18next";
import { Check, Star } from "lucide-react";
import type { LifeDomain, LifeTask } from "../lib/api";
import { dayWarsaw, dueLabel, PRIORITY_DOT } from "../lib/life";

interface LifeTaskRowProps {
  task: LifeTask;
  domains: Map<string, LifeDomain>;
  busy: boolean;
  onToggleDone: (id: string) => void;
  onToggleTop3?: (id: string) => void;
  /** Optional gray annotation on the right (e.g. "12 d temu" for slipping). */
  meta?: string;
}

export function LifeTaskRow({
  task,
  domains,
  busy,
  onToggleDone,
  onToggleTop3,
  meta,
}: LifeTaskRowProps) {
  const { t } = useTranslation("tasks");
  const done = task.status === "DONE";
  const due = done ? null : dueLabel(task.due_at);
  const domain = task.domain ? domains.get(task.domain) : undefined;
  const isTop3Today = task.is_top3 === 1 && task.top3_date === dayWarsaw();

  return (
    <div className="card-hover p-3 flex items-center gap-3">
      <button
        onClick={() => onToggleDone(task.id)}
        disabled={busy}
        title={done ? t("markOpen") : t("markDone")}
        aria-label={done ? t("markOpen") : t("markDone")}
        className={`w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 transition-colors disabled:opacity-60 ${
          done
            ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-400"
            : "border-border bg-surface-2 text-transparent hover:border-emerald-500/40"
        }`}
      >
        <Check className="w-3.5 h-3.5" />
      </button>
      <span
        className={`w-2 h-2 rounded-full flex-shrink-0 ${PRIORITY_DOT[task.priority]}`}
        title={t(`priorityNames.${task.priority}`)}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <p
          className={`text-sm truncate ${done ? "text-gray-500 line-through" : "text-gray-200"}`}
          title={task.title}
        >
          {task.title}
        </p>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px]">
          {due && (
            <span className={due.overdue ? "text-red-400 font-medium" : "text-gray-400"}>
              {due.text}
            </span>
          )}
          {domain && (
            <span className="inline-flex items-center gap-1 text-gray-400">
              <span
                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: domain.color }}
                aria-hidden="true"
              />
              {domain.name}
            </span>
          )}
          {meta && <span className="text-gray-500">{meta}</span>}
        </div>
      </div>
      {onToggleTop3 && !done && (
        <button
          onClick={() => onToggleTop3(task.id)}
          disabled={busy}
          title={t("top3Toggle")}
          aria-label={t("top3Toggle")}
          className={`p-1.5 rounded-md flex-shrink-0 transition-colors disabled:opacity-60 ${
            isTop3Today
              ? "text-amber-400 hover:text-amber-300"
              : "text-gray-600 hover:text-amber-400"
          }`}
        >
          <Star className={`w-4 h-4 ${isTop3Today ? "fill-amber-400" : ""}`} />
        </button>
      )}
    </div>
  );
}
